<template>
  <div>
    <organism-ticket-management
      v-if="!isLoading"
      :filter-list="filterData"
      :typeOption="typeOption"
      :status-option="statusOption"
      :priority-option="priorityOption"
      :department-option="departmentOption"
      :created-by-option="createdByOption"
      :fields="fields"
      :items="ticketList"
      @clickRow="clickRow"
      @onUpdateFilterData="onUpdateFilterData"
      @onUpdateCurrentPageOrPerPage="onUpdateCurrentPageOrPerPage"
      @onSetDefaultFilterData="onSetDefaultFilterData"
      @clickDelete="clickDeleteTicket"
    ></organism-ticket-management>
  </div>
</template>

<script>
import { DateTime } from "luxon"
import {
  getTicketList,
  deleteTicketById,
  getAllUserList,
} from "@/api/api-module1"
import { mapActions, mapGetters } from "vuex"
import { getAllDepartmentList } from "@/api/api-module-data"
import mixins from "@/mixins"
import mixinsObjectHelper from "@/mixins/mixinsObjectHelper"
import mixinsSiteHelper from "@/mixins/mixinsSiteHelper"
import mixinsDatetime from "@/mixins/mixinsDatetime"
import OrganismTicketManagement from "@/components/organisms/portal/internal/OrganismTicketManagement.vue"
import { TicketType } from "@/constants"

export default {
  name: "TicketManagement",
  components: { OrganismTicketManagement },
  mixins: [mixins, mixinsSiteHelper, mixinsObjectHelper, mixinsDatetime],
  data() {
    return {
      typeOption: [
        { id: TicketType.NORMAL, name: "Ticket" },
        { id: TicketType.INCIDENT, name: "Incident" },
        { id: TicketType.JOB, name: "Job" },
      ],
      statusOption: [
        { id: "OPENED", name: "Opened" },
        { id: "IN_PROGRESS", name: "In Progress" },
        { id: "CLOSED", name: "Closed" },
      ],
      priorityOption: [
        { id: "CRITICAL", name: "Critical" },
        { id: "HIGH", name: "High" },
        { id: "MEDIUM", name: "Medium" },
        { id: "LOW", name: "Low" },
      ],
      departmentOption: null,
      fields: [
        {
          key: "status",
          label: "STATUS",
          thClass: "w-100px",
          tdClass: "w-100px",
        },
        {
          key: "id",
          label: "Ticket ID",
          thClass: "w-120px",
          tdClass: "w-120px",
        },
        {
          key: "content",
          label: "SUBJECT/DETAIL",
          thClass: "w-300px",
          tdClass: "w-300px",
        },
        {
          key: "createdDate",
          label: "CREATED DATE",
          thClass: "w-150px",
          tdClass: "w-150px",
        },
        {
          key: "siteAndDepartment",
          label: "SITE/DEPARTMENT",
          thClass: "w-200px",
          tdClass: "w-200px",
        },
        {
          key: "priority",
          label: "PRIORITY",
          thClass: "w-100px",
          tdClass: "w-100px",
        },
        {
          key: "communication",
          label: "COMMUNICATION",
          thClass: "w-200px",
          tdClass: "w-200px",
        },
        {
          key: "internalThread",
          label: "INTERNAL THREAD",
          thClass: "w-200px",
          tdClass: "w-200px",
        },
        {
          key: "deleteIcon",
          label: "",
          thClass: "w-50px",
          tdClass: "w-50px",
        },
      ],
      ticketList: [],
      filterData: null,
      createdByOption: [],
    }
  },

  computed: {
    ...mapGetters("appFilter", ["getInternalTicketFilter"]),

    ...mapGetters("siteStore", ["siteSetting", "siteOptions"]),

    isLoading() {
      return !this.departmentOption || !this.ticketList
    },
  },

  watch: {
    siteSetting: {
      deep: true,
      handler: async function (newValue, oldValue) {
        if (this.compareIsChangeObject(newValue, oldValue)) {
          const updatedFilterFirstCurrentPage = {
            ...this.getInternalTicketFilter,
            currentPage: 1,
          }
          this.setInternalTicketFilter(updatedFilterFirstCurrentPage)
        }
        await this.getTicketListFromApi()
      },
    },
  },

  async mounted() {
    await this.getDataFromApi()
  },

  methods: {
    ...mapActions("appFilter", ["setInternalTicketFilter"]),

    cloneFilterDataFromVuex() {
      const filterDataFromVuex = this.getInternalTicketFilter
      this.filterData = this.deepCopyObject(filterDataFromVuex)
    },

    // get data from api
    async getDataFromApi() {
      await Promise.all([
        this.getTicketListFromApi(),
        this.getDepartmentOptionFromApi(),
        this.getCreatedByOption(),
      ])
    },

    // get department list
    async getDepartmentOptionFromApi() {
      this.showLoading(true)
      this.departmentOption = []
      const params = {
        siteIds: this.siteOptions.map((site) => site.value),
      }
      await getAllDepartmentList(params)
        .then((response) => {
          this.departmentOption = response.data?.map((item) => {
            return {
              id: item.departmentId,
              name: `${item.siteName}: ${item.departmentName}`,
            }
          })

          this.departmentOption.sort((a, b) => {
            let nameFirst = a.name.toLowerCase()
            let nameTwo = b.name.toLowerCase()
            if (nameFirst < nameTwo) return -1
            if (nameFirst > nameTwo) return 1
            return 0
          })
        })
        .finally(() => this.showLoading(false))
    },

    async getCreatedByOption() {
      this.showLoading(true)
      const params = {
        siteIds: this.siteOptions.map((site) => site.value),
      }
      await getAllUserList(params)
        .then((response) => {
          this.createdByOption = response.map((user) => ({
            username: user.username,
            name: `${user.firstName} ${user.lastName}`,
          }))
        })
        .finally(() => this.showLoading(false))
    },

    // get ticket list
    async getTicketListFromApi() {
      this.showLoading(true)
      this.cloneFilterDataFromVuex()
      const filterData = await this.filterDecoration()
      await getTicketList(filterData)
        .then(async (response) => {
          this.filterData.totalCount = response.totalCount
          const updatedFilter = {
            ...this.getInternalTicketFilter,
            totalCount: response.totalCount,
          }
          this.setInternalTicketFilter(updatedFilter)
          this.ticketList = await Promise.all(
            response.data.map(async (ticket) => {
              const siteAndDepartment =
                await this.getSiteAndDepartmentNameByDepartmentIdList([
                  ticket.responsibleDepartmentId,
                ])

              return {
                type: ticket.ticketType,
                isShowTypeOnTable:
                  ticket.ticketType === TicketType.INCIDENT ||
                  ticket.ticketType === TicketType.JOB,
                status: ticket.status,
                id: {
                  ticket: ticket.ticketId,
                  idc: ticket.contractId?.toString().padStart(8, "0"),
                  customerName: ticket.contractCustomerName,
                },
                createdDate: {
                  date: DateTime.fromISO(ticket.openedAt).toFormat(
                    "dd/MM/yyyy"
                  ),
                  time: DateTime.fromISO(ticket.openedAt).toFormat("HH:mm:ss"),
                },
                content: {
                  subject: ticket.ticketSubject,
                  detail: ticket.ticketDetail,
                },
                priority: ticket.priority,
                siteAndDepartment,
                communication: {
                  createdBy: ticket.latestThread
                    ? ticket.latestThread.createdBy
                    : null,
                  createdAt: DateTime.fromISO(
                    ticket.latestThread?.createdAt
                  ).toFormat("dd/MM/yyyy HH:mm:ss"),
                  threadMessage: ticket.latestThread?.threadMessage,
                },
                internalThread: {
                  createdBy: ticket.latestThreadInternal
                    ? ticket.latestThreadInternal.createdBy
                    : null,
                  createdAt: DateTime.fromISO(
                    ticket.latestThreadInternal?.createdAt
                  ).toFormat("dd/MM/yyyy HH:mm:ss"),
                  internalThreadMessage:
                    ticket.latestThreadInternal?.threadMessage,
                },
              }
            })
          )
        })
        .finally(() => this.showLoading(false))
    },

    filterDecoration() {
      const filter = {
        PageSize: this.filterData.perPage,
        PageNumber: this.filterData.currentPage,
        TicketTypes: this.filterData.type?.length
          ? this.filterData.type.map((item) => item.id)
          : undefined,
        TicketId: this.filterData.ticketId || undefined,
        ContractId:
          this.filterData.idcId.toLowerCase().replace("idc", "") || undefined,
        StartDate: this.filterData.isEnableDate
          ? this.removeTimeZone(this.filterData.dateTime.start)
          : undefined,
        EndDate: this.filterData.isEnableDate
          ? this.removeTimeZone(this.filterData.dateTime.end)
          : undefined,
        Priority:
          this.filterData.priority.map((item) => item.id).join(",") ||
          undefined,
        Status:
          this.filterData.status.map((item) => item.id).join(",") || undefined,
        Department:
          this.filterData.department.map((item) => item.id).join(",") ||
          undefined,
        Search: this.filterData.subjectDetail || undefined,
        SiteIds: this.siteOptions.map((site) => site.value),
        OpenedBys: this.filterData.createdBy
          ? [this.filterData.createdBy.username]
          : undefined,
      }
      return filter
    },

    // click filter
    onUpdateFilterData(filter) {
      filter.currentPage = 1
      const updatedFilter = this.deepCopyObject(filter)
      this.setInternalTicketFilter(updatedFilter)
      this.getTicketListFromApi()
    },

    onUpdateCurrentPageOrPerPage(filter) {
      const updatedFilter = {
        ...this.getInternalTicketFilter,
        currentPage: filter.currentPage,
        perPage: filter.perPage,
      }
      this.setInternalTicketFilter(updatedFilter)
      this.getTicketListFromApi()
    },

    onSetDefaultFilterData() {
      const defaultFilter = {
        type: [],
        status: [
          { id: "OPENED", name: "Opened" },
          { id: "IN_PROGRESS", name: "In Progress" },
        ],
        priority: [],
        ticketId: "",
        idcId: "",
        subjectDetail: "",
        department: [],
        dateTime: {
          start: DateTime.now().startOf("day").toISO(),
          end: DateTime.now().toISO(),
          now: DateTime.now().toISO(),
        },
        isEnableDate: false,
        currentPage: 1,
        totalCount: 0,
        perPage: 10,
      }
      this.setInternalTicketFilter(defaultFilter)
      this.getTicketListFromApi()
    },

    // click row
    clickRow(record, index) {
      this.$router.push(`/internal/web-portal/ticket/${record.id.ticket}`)
    },
    // click delete
    async clickDeleteTicket(ticketId) {
      this.showLoading(true)
      await deleteTicketById(ticketId)
        .then(async (response) => {
          if (response.status === "ok") {
            this.toastSuccess("Ticket deleted successfully")

            await this.getDataFromApi()
          } else this.toastError("Ticket deleted failed")
        })
        .finally(() => this.showLoading(false))
    },
  },
}
</script>

<style lang="scss" scoped></style>
